import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import {
  ColorIndicator,
  DesktopSelectorWrapper,
  Input,
  InputWrapper,
  MobileColorPickerWrapper,
  MobileResetButton,
  MobileSelectorWrapper,
  Button,
  Icon,
  PaletteWrapper,
  FullDesktopWrapper,
} from "./styled";
import { SketchPicker, ColorResult, RGBColor } from "react-color";
import { useMediaQuery } from "react-responsive";
import { MOBILE_VIEW } from "../../variables";
import { getIcon } from "../../utils/get-icon";
import {
  formatColorDisplayPart,
  hexToRgb,
  getCurrentColorComponents,
  constructColorResult,
  parseRgbaString,
  RgbaColor,
} from "./helpers";
import { useDetectClickOutside } from "react-detect-click-outside";

interface IColorSelectProps {
  onChange: (color: ColorResult) => void;
  color: string;
  onReset: () => void;
}

const ColorSelect: React.FC<IColorSelectProps> = ({
  onChange: parentOnChange,
  color: colorProp,
  onReset,
}) => {
  const isMobile = useMediaQuery({ query: `(max-width: ${MOBILE_VIEW}px)` });
  const [open, setOpen] = useState<boolean>(false);

  const ref = useDetectClickOutside({ onTriggered: () => setOpen(false) });

  const [localHex, setLocalHex] = useState<string>(
    () => formatColorDisplayPart(colorProp, "color") as string
  );
  const [localOpacity, setLocalOpacity] = useState<string>(() =>
    (formatColorDisplayPart(colorProp, "opacity") as number).toString()
  );

  useEffect(() => {
    const newDisplayHex = formatColorDisplayPart(colorProp, "color") as string;
    const newDisplayOpacity = (
      formatColorDisplayPart(colorProp, "opacity") as number
    ).toString();

    setLocalHex(newDisplayHex);
    setLocalOpacity(newDisplayOpacity);
  }, [colorProp]);

  const clickSelect = () => setOpen(!open);
  const clickReset = () => {
    setOpen(false);
    onReset();
  };

  const attemptCommitHex = (hexToCommit: string) => {
    const newRgb = hexToRgb(hexToCommit);
    if (newRgb) {
      const currentAlpha = getCurrentColorComponents(colorProp).a;
      const newColorResult = constructColorResult(
        newRgb.r,
        newRgb.g,
        newRgb.b,
        currentAlpha
      );
      parentOnChange(newColorResult);
    } else {
      setLocalHex(formatColorDisplayPart(colorProp, "color") as string);
    }
  };

  const attemptCommitOpacity = (opacityStringToCommit: string) => {
    const newOpacityPercent = parseFloat(opacityStringToCommit);
    if (
      !isNaN(newOpacityPercent) &&
      newOpacityPercent >= 0 &&
      newOpacityPercent <= 100
    ) {
      const newAlpha = newOpacityPercent / 100;
      const currentRgb = getCurrentColorComponents(colorProp);
      const newColorResult = constructColorResult(
        currentRgb.r,
        currentRgb.g,
        currentRgb.b,
        newAlpha
      );
      parentOnChange(newColorResult);
    } else {
      setLocalOpacity(
        (formatColorDisplayPart(colorProp, "opacity") as number).toString()
      );
    }
  };

  const handleLocalHexChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalHex(e.target.value);
  };

  const handleLocalOpacityChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLocalOpacity(e.target.value);
  };

  const handleHexKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      attemptCommitHex(localHex);
    }
  };

  const handleOpacityKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    attemptCommitOpacity(localOpacity);
  };

  const handlePickerChange = (pickerColorResult: ColorResult) => {
    parentOnChange(pickerColorResult);
  };

  const getPickerColorForSketch = (): string | RGBColor => {
    const parsed = parseRgbaString(colorProp) as RgbaColor | null;
    if (parsed) return parsed;
    if (
      !colorProp ||
      colorProp.toLowerCase() === "none" ||
      colorProp.toLowerCase() === "transparent"
    ) {
      return "rgba(0,0,0,0)";
    }
    return colorProp;
  };

  return isMobile ? (
    <MobileSelectorWrapper>
      <MobileColorPickerWrapper>
        <SketchPicker
          color={getPickerColorForSketch()}
          onChange={handlePickerChange}
        />
      </MobileColorPickerWrapper>
      <MobileResetButton onClick={clickReset}>
        {getIcon("remove")}
      </MobileResetButton>
    </MobileSelectorWrapper>
  ) : (
    <FullDesktopWrapper ref={ref}>
      <DesktopSelectorWrapper>
        <InputWrapper className="color">
          <ColorIndicator
            onClick={clickSelect}
            style={{
              backgroundColor:
                localHex === "Transparent" || localHex === "Ingen"
                  ? "rgba(0,0,0,0)"
                  : localHex,
            }}
          />
          <Input
            type="text"
            value={localHex === "Transparent" ? "Ingen" : localHex}
            onChange={handleLocalHexChange}
            onKeyDown={handleHexKeyDown}
            onClick={clickSelect}
            placeholder={
              localHex === "Transparent" || localHex === "Ingen"
                ? localHex
                : "#RRGGBB"
            }
          />
          <Button onClick={clickReset}>{getIcon("exit")}</Button>
        </InputWrapper>
        <InputWrapper className="alpha">
          <Input
            type="number"
            min={0}
            max={100}
            step={1}
            value={localOpacity}
            onClick={clickSelect}
            onChange={handleLocalOpacityChange}
            onKeyDown={handleOpacityKeyDown}
          />
          <Icon onClick={clickSelect}>{getIcon("percentage")}</Icon>
        </InputWrapper>
      </DesktopSelectorWrapper>
      {open && (
        <PaletteWrapper>
          <SketchPicker
            color={getPickerColorForSketch()}
            onChange={handlePickerChange}
          />
        </PaletteWrapper>
      )}
    </FullDesktopWrapper>
  );
};

export default ColorSelect;
